import React, { useEffect, useRef, useState } from 'react';
import {
  OrderEditingCatalogLoader,
  OrderEditingCatalogVariant,
} from 'types/order-editing';
import { inputClass } from './WorkspaceSections';

const label = (variant: OrderEditingCatalogVariant) =>
  `${variant.productTitle} — ${variant.title}`;

export default function ApprovedProductPicker({
  selectedIds,
  onChange,
  loadCatalog,
  disabled,
}: {
  selectedIds: string[];
  onChange: (ids: string[]) => void;
  loadCatalog?: OrderEditingCatalogLoader;
  disabled: boolean;
}) {
  const [search, setSearch] = useState('');
  const [submittedSearch, setSubmittedSearch] = useState('');
  const [results, setResults] = useState<OrderEditingCatalogVariant[]>([]);
  const [known, setKnown] = useState<
    Record<string, OrderEditingCatalogVariant>
  >({});
  const [cursor, setCursor] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [hydrating, setHydrating] = useState(false);
  const [searched, setSearched] = useState(false);
  const [error, setError] = useState('');
  const [selectionError, setSelectionError] = useState('');
  const [retry, setRetry] = useState(0);
  const generation = useRef(0);
  const resolvedIds = useRef(new Set<string>());
  const selectedKey = JSON.stringify(selectedIds);

  useEffect(() => {
    generation.current += 1;
    setResults([]);
    setKnown({});
    resolvedIds.current = new Set();
    setCursor(null);
    setSearched(false);
    setLoading(false);
    setSearch('');
    setError('');
    return () => {
      generation.current += 1;
    };
  }, [loadCatalog]);

  useEffect(() => {
    let active = true;
    const ids: string[] = (JSON.parse(selectedKey) as string[]).filter(
      (id) => !resolvedIds.current.has(id),
    );
    setSelectionError('');
    if (!loadCatalog || !ids.length) {
      setHydrating(false);
      return;
    }
    setHydrating(true);
    loadCatalog({ variantIds: ids })
      .then((page) => {
        if (!active) return;
        if (
          !Array.isArray(page?.variants) ||
          page.variants.some(
            (item) => !item || typeof item.id !== 'string' || !item.id.trim(),
          )
        )
          throw new Error('Invalid catalog');
        ids.forEach((id) => resolvedIds.current.add(id));
        setKnown((previous) => ({
          ...previous,
          ...Object.fromEntries(page.variants.map((item) => [item.id, item])),
        }));
      })
      .catch(() => {
        if (active)
          setSelectionError(
            'Unable to load selected product details. Your selections are preserved.',
          );
      })
      .finally(() => {
        if (active) setHydrating(false);
      });
    return () => {
      active = false;
    };
  }, [loadCatalog, selectedKey, retry]);

  const find = async (more = false) => {
    if (!loadCatalog || loading || disabled) return;
    const current = generation.current;
    const query = more ? submittedSearch : search.trim();
    setLoading(true);
    setError('');
    try {
      const page = await loadCatalog({
        search: query,
        ...(more && cursor ? { after: cursor } : {}),
      });
      if (current !== generation.current) return;
      if (
        !Array.isArray(page?.variants) ||
        !page.pageInfo ||
        page.variants.some(
          (item) => !item || typeof item.id !== 'string' || !item.id.trim(),
        )
      )
        throw new Error('Invalid catalog');
      page.variants.forEach((variant) => resolvedIds.current.add(variant.id));
      setResults((previous) =>
        Array.from(
          new Map(
            [...(more ? previous : []), ...page.variants].map((item) => [
              item.id,
              item,
            ]),
          ).values(),
        ),
      );
      setKnown((previous) => ({
        ...previous,
        ...Object.fromEntries(page.variants.map((item) => [item.id, item])),
      }));
      setCursor(page.pageInfo.hasNextPage ? page.pageInfo.endCursor : null);
      setSubmittedSearch(query);
      setSearched(true);
    } catch (_) {
      if (current === generation.current)
        setError('Unable to load products. Please search again.');
    } finally {
      if (current === generation.current) setLoading(false);
    }
  };

  return (
    <div className="mt-6 border-t border-gray-200 pt-5">
      <h3 className="text-sm font-semibold">Approved products</h3>
      <p className="mt-1 text-xs leading-5 text-gray-600">
        Choose up to 200 variants for additions and swaps. A swap stays within
        the original product. Availability and prices are checked again when the
        customer edits an order.
      </p>
      <div className="mt-3 grid gap-5 lg:grid-cols-2">
        <div>
          <label
            className="text-sm font-medium"
            htmlFor="order-edit-product-search"
          >
            Search products or SKU
          </label>
          <div className="mt-1 flex items-center gap-2">
            <input
              id="order-edit-product-search"
              type="search"
              maxLength={120}
              className={inputClass}
              value={search}
              disabled={disabled || !loadCatalog}
              onChange={(event) => setSearch(event.target.value)}
              onKeyDown={(event) => {
                if (event.key === 'Enter') {
                  event.preventDefault();
                  void find();
                }
              }}
            />
            <button
              type="button"
              className="shrink-0 rounded-md border border-gray-300 px-3 py-2 text-sm disabled:opacity-50"
              disabled={disabled || loading || !loadCatalog}
              onClick={() => void find()}
            >
              Search products
            </button>
          </div>
          {!loadCatalog && (
            <p className="mt-2 text-xs text-gray-600">
              Product search is unavailable. Reload settings to try again.
            </p>
          )}
          {error && (
            <p role="alert" className="mt-2 text-xs text-red-700">
              {error}
            </p>
          )}
          {loading && (
            <p role="status" className="mt-2 text-xs text-gray-600">
              Loading products…
            </p>
          )}
          {!searched && !loading && (
            <p className="mt-2 text-xs text-gray-500">
              Search by name or SKU, or search with an empty field to browse.
            </p>
          )}
          {searched && !loading && !results.length && (
            <p className="mt-2 text-sm text-gray-600">
              No matching variants found.
            </p>
          )}
          {!!results.length && (
            <ul className="mt-3 max-h-72 divide-y divide-gray-100 overflow-y-auto rounded-md border border-gray-200">
              {results.map((variant) => (
                <li key={variant.id} className="p-3">
                  <label className="flex items-start gap-3 text-sm">
                    <input
                      type="checkbox"
                      className="mt-1 rounded border-gray-300"
                      aria-label={`Approve ${label(variant)}`}
                      checked={selectedIds.includes(variant.id)}
                      disabled={
                        disabled ||
                        (!selectedIds.includes(variant.id) &&
                          (!variant.selectable || selectedIds.length >= 200))
                      }
                      onChange={(event) =>
                        onChange(
                          event.target.checked
                            ? [...selectedIds, variant.id]
                            : selectedIds.filter((id) => id !== variant.id),
                        )
                      }
                    />
                    <span>
                      <span className="block font-medium">
                        {variant.productTitle}
                      </span>
                      <span className="block text-xs text-gray-600">
                        {variant.title}
                        {variant.sku ? ` · ${variant.sku}` : ''} ·{' '}
                        {variant.price} {variant.currency}
                      </span>
                      {variant.unavailableReason && (
                        <span className="block text-xs text-amber-800">
                          {variant.unavailableReason}
                        </span>
                      )}
                    </span>
                  </label>
                </li>
              ))}
            </ul>
          )}
          {cursor && (
            <button
              type="button"
              className="mt-2 text-sm font-medium text-blue-700 disabled:opacity-50"
              disabled={disabled || loading}
              onClick={() => void find(true)}
            >
              Load more products
            </button>
          )}
        </div>
        <div>
          <h4 className="text-sm font-medium">
            Selected variants ({selectedIds.length}/200)
          </h4>
          {selectionError && (
            <div role="alert" className="mt-2 text-xs text-red-700">
              {selectionError}{' '}
              <button
                type="button"
                className="underline"
                disabled={disabled || hydrating}
                onClick={() => setRetry((value) => value + 1)}
              >
                Retry selected products
              </button>
            </div>
          )}
          {!selectedIds.length && (
            <p className="mt-2 text-xs text-gray-500">
              Select variants to make them available for approved additions and
              same-product swaps.
            </p>
          )}
          {!!selectedIds.length && (
            <ul className="mt-3 max-h-72 divide-y divide-gray-100 overflow-y-auto rounded-md border border-gray-200">
              {selectedIds.map((id) => {
                const variant = known[id];
                return (
                  <li
                    key={id}
                    className="flex items-start justify-between gap-3 p-3 text-sm"
                  >
                    <span>
                      {variant ? (
                        <>
                          <span className="block font-medium">
                            {variant.productTitle}
                          </span>
                          <span className="block text-xs text-gray-600">
                            {variant.title}
                          </span>
                          {variant.unavailableReason && (
                            <span className="block text-xs text-amber-800">
                              {variant.unavailableReason}
                            </span>
                          )}
                        </>
                      ) : (
                        <>
                          <span className="block">
                            {hydrating
                              ? 'Loading selected variant…'
                              : selectionError
                              ? 'Product details unavailable'
                              : 'Unavailable variant'}
                          </span>
                          <span className="break-all text-xs text-gray-500">
                            {id}
                          </span>
                        </>
                      )}
                    </span>
                    <button
                      type="button"
                      className="text-xs font-medium text-blue-700 disabled:opacity-50"
                      disabled={disabled}
                      aria-label={
                        variant
                          ? `Remove ${label(variant)}`
                          : `Remove unavailable variant ${id}`
                      }
                      onClick={() =>
                        onChange(
                          selectedIds.filter((selected) => selected !== id),
                        )
                      }
                    >
                      Remove
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
}
